import DOMPurify from "dompurify";

/**
 * Enhanced HTML sanitization to prevent XSS attacks
 */
export const sanitizeHtml = (htmlContent: string): string => {
  if (!htmlContent || typeof htmlContent !== "string") {
    return "";
  }

  return DOMPurify.sanitize(htmlContent, {
    ALLOWED_TAGS: [
      "p",
      "br",
      "strong",
      "em",
      "u",
      "code",
      "pre",
      "ul",
      "ol",
      "li",
      "a",
      "blockquote",
      "h1",
      "h2",
      "h3",
      "h4",
      "h5",
      "h6",
      "span",
      "div",
    ],
    ALLOWED_ATTR: ["href", "target", "class"],
    ALLOW_DATA_ATTR: false,
    FORBID_TAGS: [
      "script",
      "object",
      "embed",
      "form",
      "input",
      "textarea",
      "button",
      "iframe",
      "frame",
      "frameset",
      "applet",
      "meta",
      "link",
      "style",
      "base",
      "head",
      "title",
      "body",
      "html",
    ],
    FORBID_ATTR: [
      "onerror",
      "onload",
      "onclick",
      "onmouseover",
      "onmouseout",
      "onfocus",
      "onblur",
      "onchange",
      "onsubmit",
      "onreset",
      "onkeydown",
      "onkeyup",
      "onkeypress",
      "style",
      "id",
      "name",
    ],
    FORCE_BODY: true,
    SANITIZE_DOM: true,
    KEEP_CONTENT: true,
    // Add hooks to further customize sanitization
    ADD_ATTR: [],
    // Transform links to be safer
    ALLOWED_URI_REGEXP:
      /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp|xxx):|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/i,
  });
};

/**
 * Sanitize user input for database queries and API calls
 */
export const sanitizeInput = {
  /**
   * Remove potentially dangerous SQL injection patterns
   */
  sql: (input: string): string => {
    if (!input || typeof input !== "string") return "";

    // Remove common SQL injection patterns
    const sqlPatterns = [
      /(\b(ALTER|CREATE|DELETE|DROP|EXEC(UTE)?|INSERT|MERGE|SELECT|UPDATE|UNION|SCRIPT|DECLARE)\b)/gi,
      /(--|\/\*|\*\/|;|'|"|\||&|<|>|=|\+)/g,
      /(\bOR\b|\bAND\b)/gi,
    ];

    let sanitized = input;
    sqlPatterns.forEach((pattern) => {
      sanitized = sanitized.replace(pattern, "");
    });

    return sanitized.trim();
  },

  /**
   * Sanitize general text input
   */
  text: (input: string, maxLength = 1000): string => {
    if (!input || typeof input !== "string") return "";

    // Remove potentially dangerous characters and patterns
    let sanitized = input
      // eslint-disable-next-line no-control-regex
      .replace(/[\x00-\x1F\x7F]/g, "") // Remove control characters
      .replace(/<script[\s\S]*?<\/script>/gi, "") // Remove script tags
      .replace(/javascript:/gi, "") // Remove javascript: protocols
      .replace(/on\w+\s*=/gi, "") // Remove event handlers
      .trim();

    // Limit length
    if (sanitized.length > maxLength) {
      sanitized = sanitized.substring(0, maxLength);
    }

    return sanitized;
  },

  /**
   * Sanitize file names
   */
  filename: (filename: string): string => {
    if (!filename || typeof filename !== "string") return "";

    return filename
      .replace(/[^a-zA-Z0-9._-]/g, "") // Only allow alphanumeric, dots, underscores, hyphens
      .replace(/\.{2,}/g, ".") // Replace multiple dots with single dot
      .replace(/^\.+|\.+$/g, "") // Remove leading/trailing dots
      .substring(0, 255); // Limit length
  },

  /**
   * Sanitize URLs
   */
  url: (url: string): string => {
    if (!url || typeof url !== "string") return "";

    try {
      const urlObj = new URL(url);

      // Only allow HTTP(S) protocols
      if (!["http:", "https:"].includes(urlObj.protocol)) {
        return "";
      }

      return urlObj.toString();
    } catch {
      return "";
    }
  },

  /**
   * Sanitize email addresses
   */
  email: (email: string): string => {
    if (!email || typeof email !== "string") return "";

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email.trim()) ? email.trim().toLowerCase() : "";
  },
};

/**
 * Secure token storage utility with improved security
 * Uses sessionStorage with multi-layer encoding and integrity checks
 */
class SecureTokenStorage {
  private readonly tokenKey = "nopal_auth_token";
  private readonly storagePrefix = "secure_";
  private readonly hashKey = "token_hash";

  /**
   * Generate a simple hash for integrity checking
   */
  private simpleHash(value: string): string {
    let hash = 0;
    for (let i = 0; i < value.length; i++) {
      const char = value.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return hash.toString(16);
  }

  /**
   * Multi-layer encoding with timestamp and integrity check
   */
  private encode(value: string): string {
    const timestamp = Date.now().toString();
    const payload = JSON.stringify({ value, timestamp });
    const hash = this.simpleHash(payload);
    const combined = JSON.stringify({ payload, hash });
    return btoa(encodeURIComponent(combined));
  }

  private decode(encoded: string): string {
    try {
      const decoded = decodeURIComponent(atob(encoded));
      const { payload, hash } = JSON.parse(decoded);

      // Verify integrity
      if (this.simpleHash(payload) !== hash) {
        console.warn("Token integrity check failed");
        return "";
      }

      const { value, timestamp } = JSON.parse(payload);

      // Check if token is too old (24 hours)
      const age = Date.now() - parseInt(timestamp);
      const maxAge = 24 * 60 * 60 * 1000; // 24 hours
      if (age > maxAge) {
        console.warn("Token expired due to age");
        this.removeToken();
        return "";
      }

      return value;
    } catch (error) {
      console.warn("Failed to decode token:", error);
      return "";
    }
  }

  /**
   * Store auth token securely
   */
  setToken(token: string): void {
    if (!token) return;

    try {
      const encoded = this.encode(token);
      sessionStorage.setItem(this.storagePrefix + this.tokenKey, encoded);
    } catch (error) {
      console.warn("Failed to store auth token securely");
    }
  }

  /**
   * Retrieve auth token securely
   */
  getToken(): string | null {
    try {
      const encoded = sessionStorage.getItem(
        this.storagePrefix + this.tokenKey
      );
      return encoded ? this.decode(encoded) : null;
    } catch (error) {
      console.warn("Failed to retrieve auth token securely");
      return null;
    }
  }

  /**
   * Remove auth token
   */
  removeToken(): void {
    try {
      sessionStorage.removeItem(this.storagePrefix + this.tokenKey);
      // Also clean up any old localStorage tokens for migration
      localStorage.removeItem("auth_token");
    } catch (error) {
      console.warn("Failed to remove auth token securely");
    }
  }

  /**
   * Check if token exists
   */
  hasToken(): boolean {
    return this.getToken() !== null;
  }
}

export const secureTokenStorage = new SecureTokenStorage();

/**
 * Input validation utilities
 */
export const validateInput = {
  /**
   * Validate chat message input
   */
  chatMessage: (message: string): { isValid: boolean; error?: string } => {
    if (!message || typeof message !== "string") {
      return { isValid: false, error: "Message cannot be empty" };
    }

    const trimmed = message.trim();
    if (trimmed.length === 0) {
      return { isValid: false, error: "Message cannot be empty" };
    }

    if (trimmed.length > 4000) {
      return {
        isValid: false,
        error: "Message is too long (max 4000 characters)",
      };
    }

    // Check for potentially dangerous patterns
    const dangerousPatterns = [
      /<script[\s\S]*?<\/script>/gi,
      /javascript:/gi,
      /on\w+\s*=/gi,
      /<iframe[\s\S]*?>/gi,
    ];

    const hasDangerousContent = dangerousPatterns.some((pattern) =>
      pattern.test(trimmed)
    );

    if (hasDangerousContent) {
      return {
        isValid: false,
        error: "Message contains potentially unsafe content",
      };
    }

    return { isValid: true };
  },

  /**
   * Validate file upload
   */
  file: (file: File): { isValid: boolean; error?: string } => {
    if (!file) {
      return { isValid: false, error: "No file provided" };
    }

    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      return { isValid: false, error: "File size exceeds 10MB limit" };
    }

    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "text/plain",
      "text/csv",
    ];

    if (!allowedTypes.includes(file.type)) {
      return { isValid: false, error: "File type not supported" };
    }

    return { isValid: true };
  },
};

/**
 * Rate limiting utility for frontend
 */
class RateLimiter {
  private requests: Map<string, number[]> = new Map();
  private readonly maxRequests: number;
  private readonly timeWindow: number; // in milliseconds

  constructor(maxRequests = 10, timeWindowInSeconds = 60) {
    this.maxRequests = maxRequests;
    this.timeWindow = timeWindowInSeconds * 1000;
  }

  /**
   * Check if request is allowed
   */
  isAllowed(identifier: string = "global"): boolean {
    const now = Date.now();
    const requestTimes = this.requests.get(identifier) || [];

    // Remove old requests outside the time window
    const recentRequests = requestTimes.filter(
      (time) => now - time < this.timeWindow
    );

    if (recentRequests.length >= this.maxRequests) {
      return false;
    }

    // Add current request
    recentRequests.push(now);
    this.requests.set(identifier, recentRequests);

    return true;
  }

  /**
   * Get remaining requests for identifier
   */
  getRemainingRequests(identifier: string = "global"): number {
    const now = Date.now();
    const requestTimes = this.requests.get(identifier) || [];
    const recentRequests = requestTimes.filter(
      (time) => now - time < this.timeWindow
    );

    return Math.max(0, this.maxRequests - recentRequests.length);
  }

  /**
   * Get time until next request is allowed
   */
  getTimeUntilReset(identifier: string = "global"): number {
    const now = Date.now();
    const requestTimes = this.requests.get(identifier) || [];

    if (requestTimes.length === 0) return 0;

    const oldestRequest = Math.min(...requestTimes);
    const timeUntilReset = this.timeWindow - (now - oldestRequest);

    return Math.max(0, timeUntilReset);
  }
}

export const chatRateLimiter = new RateLimiter(20, 60); // 20 requests per minute

/**
 * CSRF Token Manager
 * Manages CSRF tokens for secure API requests
 */
class CSRFTokenManager {
  private readonly csrfKey = "csrf_token";
  private currentToken: string | null = null;

  /**
   * Generate a new CSRF token with fallback
   */
  generateToken(): string {
    try {
      if (typeof crypto !== "undefined" && crypto.getRandomValues) {
        const array = new Uint8Array(32);
        crypto.getRandomValues(array);
        return Array.from(array, (byte) =>
          byte.toString(16).padStart(2, "0")
        ).join("");
      }
    } catch (error) {
      console.warn(
        "Crypto API not available, using fallback for CSRF token generation"
      );
    }

    // Fallback for environments without crypto API
    return (
      Date.now().toString(16) +
      Math.random().toString(16).substr(2) +
      Math.random().toString(16).substr(2)
    );
  }

  /**
   * Get or create CSRF token
   */
  getToken(): string {
    if (!this.currentToken) {
      this.currentToken = this.generateToken();
      // Store in sessionStorage for consistency across tabs
      try {
        sessionStorage.setItem(this.csrfKey, this.currentToken);
      } catch (error) {
        console.warn("Failed to store CSRF token");
      }
    }
    return this.currentToken;
  }

  /**
   * Refresh the CSRF token
   */
  refreshToken(): string {
    this.currentToken = this.generateToken();
    try {
      sessionStorage.setItem(this.csrfKey, this.currentToken);
    } catch (error) {
      console.warn("Failed to refresh CSRF token");
    }
    return this.currentToken;
  }

  /**
   * Clear the CSRF token
   */
  clearToken(): void {
    this.currentToken = null;
    try {
      sessionStorage.removeItem(this.csrfKey);
    } catch (error) {
      console.warn("Failed to clear CSRF token");
    }
  }

  /**
   * Initialize token from storage
   */
  initialize(): void {
    try {
      this.currentToken = sessionStorage.getItem(this.csrfKey);
      if (!this.currentToken) {
        this.getToken(); // Generate new token if none exists
      }
    } catch (error) {
      console.warn("Failed to initialize CSRF token");
      this.getToken();
    }
  }
}

export const csrfManager = new CSRFTokenManager();

/**
 * Content Security Policy helpers
 */
export const cspHelpers = {
  /**
   * Generate a nonce for inline scripts with fallback
   */
  generateNonce(): string {
    try {
      if (typeof crypto !== "undefined" && crypto.getRandomValues) {
        const array = new Uint8Array(16);
        crypto.getRandomValues(array);
        return btoa(String.fromCharCode.apply(null, Array.from(array)));
      }
    } catch (error) {
      console.warn(
        "Crypto API not available, using fallback for nonce generation"
      );
    }

    // Fallback for environments without crypto API
    const randomString = Date.now().toString() + Math.random().toString(36);
    return btoa(randomString).substr(0, 16);
  },

  /**
   * Validate URL to prevent open redirects
   */
  isValidRedirectUrl(url: string): boolean {
    try {
      const urlObj = new URL(url);
      // Only allow same origin or specific trusted domains
      const allowedHosts = [
        window.location.hostname,
        "localhost",
        "127.0.0.1",
        // Add your production domains here
      ];
      return allowedHosts.includes(urlObj.hostname);
    } catch {
      return false;
    }
  },

  /**
   * Sanitize URL parameters
   */
  sanitizeUrlParams(params: URLSearchParams): URLSearchParams {
    const sanitized = new URLSearchParams();
    params.forEach((value, key) => {
      // Remove potentially dangerous parameters
      if (
        !["javascript", "vbscript", "data", "file"].some((scheme) =>
          value.toLowerCase().includes(scheme + ":")
        )
      ) {
        sanitized.set(key, value);
      }
    });
    return sanitized;
  },
};
